// Fortune's Favor Effect (Magical Amulet granting Luck/Cards)
// Features:
// 1. A glowing Celtic amulet with a 4-leaf clover materializes from thin air, drawing in luck energy.
// 2. The amulet pulses, charging with a bright flash of green and gold aura.
// 3. It erupts into a shower of magical gold coins and green clovers that fly rapidly towards the caster's avatar.
// 4. The amulet fades back into the ether.

import { Particle } from '../ParticleLayer';

const CONFIG = {
    scale: 1.6,
    materializeDuration: 1200,
    pulseDuration: 800,
    showerDuration: 2000,
    vanishDuration: 1000,
    paths: {
        amulet: '/assets/effects/fortunes_favor/amulet.svg',
    }
};

const spriteCache: Record<string, HTMLImageElement> = {};
const getSprite = (src: string): HTMLImageElement => {
    if (!spriteCache[src]) {
        const img = new Image();
        img.src = src;
        spriteCache[src] = img;
    }
    return spriteCache[src];
};

getSprite(CONFIG.paths.amulet);

// ==========================================
// PARTICLES
// ==========================================

class LuckMote {
    x: number; y: number;
    startX: number; startY: number;
    delay: number; duration: number;
    lifeTime: number = 0;
    size: number;
    color: string;

    constructor(targetX: number, targetY: number) {
        // Start far away on a random edge
        const angle = Math.random() * Math.PI * 2;
        const dist = 300 + Math.random() * 300;
        this.startX = targetX + Math.cos(angle) * dist;
        this.startY = targetY + Math.sin(angle) * dist;
        this.x = this.startX;
        this.y = this.startY;

        this.delay = Math.random() * 600;
        this.duration = 600 + Math.random() * 600;
        this.size = 4 + Math.random() * 8;

        const colors = ['#86efac', '#4ade80', '#22c55e', '#fef08a']; // Greens and golds
        this.color = colors[Math.floor(Math.random() * colors.length)];
    }

    update(dt: number, targetX: number, targetY: number): boolean {
        if (this.delay > 0) {
            this.delay -= dt;
            return true;
        }
        this.lifeTime += dt;
        let progress = this.lifeTime / this.duration;
        if (progress >= 1) progress = 1;

        // Swirling inwards motion
        const ease = Math.pow(progress, 2); // Accelerate inwards

        // Add a bit of spiral rotation
        const currentRadius = (1 - ease) * Math.hypot(this.startX - targetX, this.startY - targetY);
        const startAngle = Math.atan2(this.startY - targetY, this.startX - targetX);
        const currentAngle = startAngle + progress * Math.PI * 1.5;

        this.x = targetX + Math.cos(currentAngle) * currentRadius;
        this.y = targetY + Math.sin(currentAngle) * currentRadius;

        return progress < 1;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.delay > 0) return;
        ctx.save();
        ctx.globalCompositeOperation = 'screen';

        // FPS Optimization: Replace expensive shadowBlur with layered alpha drawing
        ctx.fillStyle = this.color;

        ctx.globalAlpha = 0.3;
        ctx.beginPath();
        // Glow layer
        ctx.arc(this.x, this.y, this.size * 2.5, 0, Math.PI * 2);
        ctx.fill();

        ctx.globalAlpha = 1.0;
        ctx.beginPath();
        // Core layer
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
    }
}

class FortuneCoin {
    x: number; y: number;
    startX: number; startY: number;
    vx: number; vy: number;
    targetX: number; targetY: number;
    delay: number;
    lifeTime: number = 0;
    size: number;
    color: string;
    isClover: boolean;
    rotation: number;
    rotSpeed: number;
    isActive: boolean = false;

    constructor(startX: number, startY: number, targetX: number, targetY: number, maxDelay: number) {
        // Erupt outwards initially (halved speed)
        const burstAngle = Math.random() * Math.PI * 2;
        const burstSpeed = 2.5 + Math.random() * 7.5;
        this.vx = Math.cos(burstAngle) * burstSpeed;
        this.vy = Math.sin(burstAngle) * burstSpeed;

        this.startX = startX;
        this.startY = startY;
        this.x = startX;
        this.y = startY;
        this.targetX = targetX;
        this.targetY = targetY;

        this.delay = Math.random() * maxDelay;
        this.size = 12 + Math.random() * 16; // Doubled size

        this.isClover = Math.random() > 0.6; // 40% chance to be a clover

        const goldColors = ['#fef08a', '#eab308', '#d97706'];
        const greenColors = ['#86efac', '#4ade80', '#22c55e'];
        this.color = this.isClover
            ? greenColors[Math.floor(Math.random() * greenColors.length)]
            : goldColors[Math.floor(Math.random() * goldColors.length)];

        this.rotation = Math.random() * Math.PI * 2;
        this.rotSpeed = (Math.random() - 0.5) * 0.2; // Halved rotation speed
    }

    update(dt: number, targetX: number, targetY: number): boolean {
        if (this.delay > 0) {
            this.delay -= dt;
            if (this.delay <= 0) this.isActive = true;
            return true;
        }

        this.lifeTime += dt;

        // Homing behavior
        // Update target (it might have moved slightly, though avatars usually static)
        this.targetX = targetX;
        this.targetY = targetY;

        const dx = this.targetX - this.x;
        const dy = this.targetY - this.y;
        const dist = Math.hypot(dx, dy);

        // If very close to target, consume
        if (dist < 60 && this.lifeTime > 200) {
            return false;
        }

        // Steer towards target (halved max speed and halved acceleration build-up)
        const homeSpeed = Math.min(12.5, this.lifeTime / 40);
        const targetAngle = Math.atan2(dy, dx);

        const idealVx = Math.cos(targetAngle) * homeSpeed;
        const idealVy = Math.sin(targetAngle) * homeSpeed;

        // Interpolate velocity for smooth curves
        this.vx += (idealVx - this.vx) * 0.1;
        this.vy += (idealVy - this.vy) * 0.1;

        this.x += this.vx;
        this.y += this.vy;

        this.rotation += this.rotSpeed;

        // Failsafe bounds check
        if (this.x < -100 || this.x > window.innerWidth + 100 || this.y < -100 || this.y > window.innerHeight + 100) {
            return false;
        }

        return true; // still alive
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (!this.isActive) return;

        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);

        // FPS Optimization: Replace shadowBlur with alpha glow
        ctx.fillStyle = this.color;
        ctx.globalAlpha = 0.3;
        ctx.beginPath();
        ctx.arc(0, 0, this.size * 1.5, 0, Math.PI * 2);
        ctx.fill();

        ctx.globalAlpha = 1.0;

        if (this.isClover) {
            // Draw a tiny 4-leaf clover
            ctx.fillStyle = this.color;
            ctx.beginPath();
            const s = this.size / 2;
            ctx.arc(-s, -s, s, 0, Math.PI * 2);
            ctx.arc(s, -s, s, 0, Math.PI * 2);
            ctx.arc(-s, s, s, 0, Math.PI * 2);
            ctx.arc(s, s, s, 0, Math.PI * 2);
            ctx.fill();

            // tiny stem
            ctx.strokeStyle = '#14532d';
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.lineTo(0, this.size * 1.5);
            ctx.stroke();

        } else {
            // Draw a gold coin (ellipse to simulate 3D rotation)
            ctx.fillStyle = this.color;
            ctx.beginPath();
            // Scale based on rotation for 3D flip effect (slowed down twice)
            const scaleY = Math.abs(Math.sin(this.lifeTime / 200));
            ctx.ellipse(0, 0, this.size, this.size * Math.max(0.1, scaleY), 0, 0, Math.PI * 2);
            ctx.fill();

            ctx.strokeStyle = '#78350f';
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.ellipse(0, 0, this.size * 0.7, this.size * 0.7 * Math.max(0.1, scaleY), 0, 0, Math.PI * 2);
            ctx.stroke();
        }

        ctx.restore();
    }
}

export type FortunesFavorEffect = {
    kind: 'fortunesFavor'
    t: number
    tx: number; ty: number; // Target
    data?: {
        phase: 'MATERIALIZE' | 'PULSE' | 'SHOWER' | 'VANISH'
        motes: LuckMote[]
        coins: FortuneCoin[]
        centerX: number
        centerY: number
    }
}

// Center of the screen spawn, then shower to target `tx, ty`
export const createFortunesFavorEffect = (tx: number, ty: number): FortunesFavorEffect => ({
    kind: 'fortunesFavor', t: 0, tx, ty
});

export const renderFortunesFavorEffect = (
    eff: FortunesFavorEffect,
    ctx: CanvasRenderingContext2D,
    spawnParticle: (p: Particle) => void
): boolean => {
    if (!eff.data) {
        const centerX = window.innerWidth / 2;
        const centerY = window.innerHeight / 2 - 50; // Slightly above center

        const motes: LuckMote[] = [];
        for (let i = 0; i < 60; i++) {
            motes.push(new LuckMote(centerX, centerY));
        }

        eff.data = {
            phase: 'MATERIALIZE',
            motes,
            coins: [],
            centerX,
            centerY
        };
    }

    const d = eff.data;
    const dt = 16.7;

    const tMat = CONFIG.materializeDuration;
    const tPulse = tMat + CONFIG.pulseDuration;
    const tShower = tPulse + CONFIG.showerDuration;
    const tTotal = tShower + CONFIG.vanishDuration;

    if (eff.t < tMat) d.phase = 'MATERIALIZE';
    else if (eff.t < tPulse) d.phase = 'PULSE';
    else if (eff.t < tShower) d.phase = 'SHOWER';
    else d.phase = 'VANISH';

    ctx.save();

    let amuletAlpha = 0;
    let amuletScale = CONFIG.scale;
    let amuletRotation = 0;

    // Y hovering oscillation (slower and wider amplitude)
    const hoverY = d.centerY + Math.sin(eff.t / 600) * 20;

    if (d.phase === 'MATERIALIZE') {
        const p = eff.t / tMat;
        amuletAlpha = p;

        // Spin into existence
        const easeOut = 1 - Math.pow(1 - p, 3);
        amuletScale = CONFIG.scale * easeOut;
        amuletRotation = (1 - easeOut) * Math.PI * 4; // Spins 2 full times

        // Update and draw motes flying in
        d.motes = d.motes.filter(m => m.update(dt, d.centerX, hoverY));
        d.motes.forEach(m => m.draw(ctx));

    } else if (d.phase === 'PULSE') {
        amuletAlpha = 1;
        const p = (eff.t - tMat) / CONFIG.pulseDuration;

        // Swell up slightly then back down
        const pulseAmt = Math.sin(p * Math.PI);
        amuletScale = CONFIG.scale * (1 + 0.15 * pulseAmt);

        // Draw radiating aura
        ctx.save();
        ctx.translate(d.centerX, hoverY);
        ctx.globalCompositeOperation = 'screen';
        ctx.globalAlpha = pulseAmt * 0.8;

        const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, 500);
        grad.addColorStop(0, '#fef08a');
        grad.addColorStop(0.3, '#4ade80');
        grad.addColorStop(1, 'rgba(0,0,0,0)');

        ctx.beginPath();
        ctx.arc(0, 0, 500, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
        ctx.restore();

        // Spawn coins right at the end of the pulse
        if (p > 0.9 && d.coins.length === 0) {
            for (let i = 0; i < 40; i++) {
                // Max delay of 600ms for staggered shower
                d.coins.push(new FortuneCoin(d.centerX, hoverY, eff.tx, eff.ty, 600));
            }
        }

    } else if (d.phase === 'SHOWER') {
        amuletAlpha = 1;

        // Draw a persistent golden aura instead of shaking
        ctx.save();
        ctx.translate(d.centerX, hoverY);
        ctx.globalCompositeOperation = 'screen';
        // Pulsate the aura slightly
        const auraPulse = 0.6 + Math.sin(eff.t / 150) * 0.2;
        ctx.globalAlpha = auraPulse;

        // FPS optimized simple radial gradient
        const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, 450);
        grad.addColorStop(0, '#fef08a'); // light gold
        grad.addColorStop(0.3, '#d97706'); // dark gold
        grad.addColorStop(1, 'rgba(0,0,0,0)');

        ctx.beginPath();
        ctx.arc(0, 0, 450, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
        ctx.restore();

    } else if (d.phase === 'VANISH') {
        const p = (eff.t - tShower) / CONFIG.vanishDuration;
        amuletAlpha = 1 - p;

        // Float up and shrink
        amuletScale = CONFIG.scale * (1 - p * 0.5);
    }

    // Update and draw coins/clovers flying to target
    if (d.phase === 'SHOWER' || d.phase === 'VANISH') {
        d.coins = d.coins.filter(c => c.update(dt, eff.tx, eff.ty));
        d.coins.forEach(c => c.draw(ctx));
    }

    // Draw the Amulet
    if (amuletAlpha > 0) {
        ctx.save();
        ctx.globalAlpha = amuletAlpha;
        ctx.translate(d.centerX, hoverY);
        ctx.scale(amuletScale, amuletScale);
        ctx.rotate(amuletRotation);

        const img = getSprite(CONFIG.paths.amulet);
        if (img.complete && img.naturalWidth > 0) {
            // Determine a reasonable target size if nat sizes are huge. SVG naturally returns 600x600 based on viewBox
            ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2, img.naturalWidth, img.naturalHeight);
        }
        ctx.restore();
    }

    ctx.restore();

    return eff.t < tTotal || d.coins.length > 0; // Wait for coins to finish
};
